import '../tasks/task.css';
import './order.css';
import { Button, DatePicker, Drawer, Form, Input, InputNumber, Popconfirm, Progress, Radio, Select, Tooltip } from 'antd';
import {
    ApartmentOutlined, CalendarOutlined, CheckCircleFilled, CloseOutlined, ContactsOutlined, DeleteOutlined, ExportOutlined,
    FileTextOutlined, MinusOutlined, NumberOutlined, PlusOutlined, PrinterOutlined, RollbackOutlined, ShoppingCartOutlined, ShoppingOutlined,
    TagsOutlined, UserOutlined, WalletOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, toDayjs, toApiDate, fmtDate, fmtDateTime, fmtMoney, getStored, setStored } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import TagSelect from '@controls/tag-select/tag-select';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import UserAvatar from '../tasks/user-avatar';
import { LogoSlot, useContragentLogo } from '../contragents/contragent-logo';
import OrderPrint from './order-print';

const DEFAULT_ACCENT = '#1677ff';
const num = (v) => Number(v) || 0;
const sumsOf = (o) => ({ syma: num(o.syma), payment: num(o.payment), dolg: num(o.dolg) });
const ignoreValidation = (e) => { if (!e?.errorFields) showError(e); };

const Section = ({ icon, title, extra, children }) => (
    <div className="task-section">
        <div className="task-section-title">{icon}{title}{extra && <span className="task-section-extra">{extra}</span>}</div>
        {children}
    </div>
);

const Prop = ({ icon, label, children }) => (
    <div className="task-prop">
        <span className="task-prop-label">{icon}{label}</span>
        <div className="task-prop-value">{children}</div>
    </div>
);

// Шапка drawer: заголовок + сумма / оплачено / долг
const OrderHead = ({ title, sums }) => {
    const { syma, payment, dolg } = sums;
    const pct = syma > 0 ? Math.min(100, Math.round((payment / syma) * 100)) : 0;
    const paid = syma > 0 && dolg <= 0;
    return (
        <div className="order-head">
            <span className="order-head-title">{title}</span>
            <div className="order-head-sums">
                <span className="order-head-stat">
                    <span className="order-head-label">Сумма</span>
                    <b>{fmtMoney(syma)}</b>
                </span>
                <Tooltip title={syma > 0 ? `Оплачено ${pct}%` : undefined}>
                    <span className="order-head-stat ok">
                        <span className="order-head-label">Оплачено</span>
                        <b>{fmtMoney(payment)}</b>
                        {syma > 0 && <Progress type="circle" percent={pct} size={16} strokeWidth={14} showInfo={false} status={paid ? 'success' : 'normal'} />}
                    </span>
                </Tooltip>
                <span className={`order-head-stat${dolg > 0 ? ' debt' : ''}`}>
                    <span className="order-head-label">{dolg < 0 ? 'Переплата' : 'Долг'}</span>
                    <b>{fmtMoney(Math.abs(dolg))}</b>
                </span>
                {paid && <span className="order-paid"><CheckCircleFilled /> Оплачен</span>}
            </div>
        </div>
    );
};

// Карточка заказа. id=0 — новый; contragentId — предзаполненный контрагент
const Order = ({ id, contragentId, onClose, onSaved, onOpenContragent }) => {
    const user = useSelector((s) => s.user);
    const { dicts } = useDicts();
    const [form] = Form.useForm();
    const [item, setItem] = useState(null);
    const [orderId, setOrderId] = useState(id);
    const [sums, setSums] = useState({ syma: 0, payment: 0, dolg: 0 });
    const [contragentOptions, setContragentOptions] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [printing, setPrinting] = useState(false);
    const statusId = Form.useWatch('statusId', form);
    const docNum = Form.useWatch('docNum', form);
    const docDate = Form.useWatch('docDate', form);
    const formContragentId = Form.useWatch('contragentId', form);
    const contragentLogo = useContragentLogo(
        formContragentId,
        item?.contragentId,
        item?.contragentLogoFileId,
        item?.contragentLogoName || item?.contragentName,
    );

    const isNew = orderId === 0;
    const statusById = useMemo(() => new Map(dicts.orderStatuses.map((s) => [s.id, s])), [dicts.orderStatuses]);
    const accent = statusById.get(statusId)?.color || DEFAULT_ACCENT;

    useEffect(() => {
        if (isNew) {
            setItem({
                id: 0,
                docDate: toApiDate(dayjs()),
                docNum: 0,
                userId: user?.userId ?? 0,
                contragentId: contragentId ?? null,
                syma: 0,
                payment: 0,
                dolg: 0,
                tags: [],
            });
        } else {
            serverFetch(`order/${orderId}`)
                .then(setItem)
                .catch(showError);
        }
    }, [orderId, isNew, user, contragentId]);

    // Текущий контрагент в опциях селекта
    useEffect(() => {
        if (item?.contragentId) {
            setContragentOptions((prev) => {
                if (prev.some((o) => o.value === item.contragentId)) return prev;
                return [...prev, { value: item.contragentId, label: item.contragentName || `#${item.contragentId}` }];
            });
        }
    }, [item]);

    useEffect(() => {
        if (!item) return;
        setSums(sumsOf(item));
        form.setFieldsValue({
            docDate: toDayjs(item.docDate),
            docNum: item.docNum,
            statusId: item.statusId ?? undefined,
            contragentId: item.contragentId ?? undefined,
            userId: item.userId ?? undefined,
            departmentId: item.departmentId ?? undefined,
            comment: item.comment,
            tags: item.tags ?? [],
        });
        setDirty(false);
    }, [item, form]);

    const onContragentSearch = useCallback((text) => {
        if (!text || text.length < 2) return;
        serverFetch(`contragent/search?name=${encodeURIComponent(text)}&limit=20`)
            .then((list) => setContragentOptions(list.map((c) => ({ value: c.id, label: c.name }))))
            .catch(showError);
    }, []);

    const saveOrder = () => form.validateFields()
        .then((values) => serverFetch(isNew ? 'order' : `order/${orderId}`, {
            method: isNew ? 'POST' : 'PUT',
            bodyData: {
                ...(item ?? {}),
                docDate: toApiDate(values.docDate),
                docNum: values.docNum ?? 0,
                statusId: values.statusId ?? null,
                contragentId: values.contragentId ?? null,
                userId: values.userId ?? null,
                departmentId: values.departmentId ?? null,
                comment: values.comment ?? '',
                tags: values.tags ?? [],
            },
        }))
        .then((saved) => {
            setItem(saved);
            if (isNew) setOrderId(saved.id);
            onSaved?.(saved);
            return saved;
        });

    // Новый заказ сохраняется автоматически при добавлении первой позиции или платежа
    const ensureId = () => (isNew ? saveOrder().then((s) => s.id) : Promise.resolve(orderId));

    const onChanged = (order) => {
        setSums(sumsOf(order));
        onSaved?.(order);
    };

    const onOk = () => saveOrder().then(() => onClose?.()).catch(ignoreValidation);

    // Ctrl/Cmd+Enter или Ctrl/Cmd+S — сохранить
    const onKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.key === 's' || e.key === 'ы')) {
            e.preventDefault();
            onOk();
        }
    };

    const selectOpts = (list) => list.map((d) => ({ value: d.id, label: d.name }));
    const userOpts = dicts.users.map((u) => ({
        value: u.id,
        search: u.name,
        label: <span className="task-opt"><UserAvatar name={u.name} size={20} />{u.name}</span>,
    }));
    const statusOpts = dicts.orderStatuses.map((s) => ({
        value: s.id,
        label: <span className="task-opt"><i className="task-status-dot" style={{ background: s.color || '#bfbfbf' }} />{s.name}</span>,
    }));
    const title = isNew ? 'Новый заказ' : `Заказ №${docNum || orderId}`;

    return (
        <ResizableDrawer
            open
            storageKey="orderDrawerWidth"
            defaultWidth={1200}
            title={<OrderHead title={title} sums={sums} />}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer order-drawer"
        >
            {item && (
                <Form form={form} component={false} onValuesChange={() => setDirty(true)}>
                    <div className="task-layout" onKeyDown={onKeyDown}>
                        <div className="task-scroll order-scroll" style={{ '--accent': accent }}>
                            <div className="task-hero">
                                <div className="order-hero-top">
                                    {(contragentLogo.fileId || contragentLogo.name)
                                        ? <LogoSlot fileId={contragentLogo.fileId} name={contragentLogo.name} size={42} />
                                        : <div className="order-hero-icon"><ShoppingCartOutlined /></div>}
                                    <div className="order-hero-main">
                                        <div className="order-hero-title">{title}</div>
                                        <div className="task-meta order-meta">
                                            {!isNew && <span className="task-chip">#{orderId}</span>}
                                            {docDate && <span>от {fmtDate(docDate)}</span>}
                                            {item.createdOn && <span>создан {fmtDate(item.createdOn)}</span>}
                                            {!isNew && item.modifiedOn && <span>изменён {fmtDateTime(item.modifiedOn)}</span>}
                                        </div>
                                    </div>
                                    <Form.Item name="statusId" noStyle>
                                        <Select
                                            allowClear
                                            placeholder="Статус"
                                            variant="filled"
                                            className="order-status"
                                            style={{ '--status-color': statusById.get(statusId)?.color || '#8c8c8c' }}
                                            options={statusOpts}
                                            popupMatchSelectWidth={false}
                                        />
                                    </Form.Item>
                                </div>
                            </div>

                            <div className="task-section task-props">
                                <div className="task-props-grid">
                                    <Prop icon={<ContactsOutlined />} label="Контрагент">
                                        <div className="task-inline">
                                            {(contragentLogo.fileId || contragentLogo.name) && (
                                                <LogoSlot fileId={contragentLogo.fileId} name={contragentLogo.name} size={28} />
                                            )}
                                            <Form.Item name="contragentId" noStyle>
                                                <Select
                                                    allowClear
                                                    showSearch
                                                    variant="borderless"
                                                    filterOption={false}
                                                    onSearch={onContragentSearch}
                                                    options={contragentOptions}
                                                    placeholder="Начните вводить"
                                                    style={{ flex: 1, minWidth: 0 }}
                                                />
                                            </Form.Item>
                                            {onOpenContragent && formContragentId && (
                                                <Tooltip title="Открыть контрагента">
                                                    <Button
                                                        type="text" size="small" icon={<ExportOutlined />}
                                                        onClick={() => onOpenContragent(formContragentId)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </Prop>
                                    <Prop icon={<UserOutlined />} label="Исполнитель">
                                        <Form.Item name="userId" noStyle>
                                            <Select
                                                allowClear showSearch variant="borderless" placeholder="—"
                                                optionFilterProp="search" options={userOpts}
                                            />
                                        </Form.Item>
                                    </Prop>
                                    <Prop icon={<ApartmentOutlined />} label="Отдел">
                                        <Form.Item name="departmentId" noStyle>
                                            <Select allowClear variant="borderless" placeholder="—" options={selectOpts(dicts.departments)} />
                                        </Form.Item>
                                    </Prop>
                                    <Prop icon={<TagsOutlined />} label="Метки">
                                        <Form.Item name="tags" noStyle>
                                            <TagSelect tags={dicts.tags} vid={5} variant="borderless" placeholder="—" />
                                        </Form.Item>
                                    </Prop>
                                    <Prop icon={<CalendarOutlined />} label="Дата">
                                        <Form.Item name="docDate" noStyle>
                                            <DatePicker format="DD.MM.YYYY" variant="borderless" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Prop>
                                    <Prop icon={<NumberOutlined />} label="Номер">
                                        <Form.Item name="docNum" noStyle>
                                            <InputNumber min={0} variant="borderless" controls={false} placeholder="—" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Prop>
                                </div>
                            </div>
                            <Section icon={<FileTextOutlined />} title="Комментарий">
                                <Form.Item name="comment">
                                    <Input.TextArea
                                        variant="filled"
                                        autoSize={{ minRows: 3, maxRows: 12 }}
                                        placeholder="Условия, адрес доставки, пожелания клиента…"
                                    />
                                </Form.Item>
                            </Section>
                            <div className="task-actions">
                                <Button type="primary" size="large" className="task-ok" disabled={!dirty} onClick={onOk}>
                                    ОК
                                    {dirty && <span className="task-dirty-dot" />}
                                </Button>
                                <Button size="large" onClick={() => onClose?.()}>Закрыть</Button>
                                <Button size="large" icon={<PrinterOutlined />} disabled={isNew} onClick={() => setPrinting(true)}>
                                    Печать
                                </Button>
                                <span className="task-actions-hint">
                                    {dirty ? 'Есть несохранённые изменения · Ctrl+Enter — сохранить' : 'Позиции и платежи сохраняются сразу'}
                                </span>
                            </div>
                            <OrderItems orderId={isNew ? 0 : orderId} ensureId={ensureId} onChanged={onChanged} />
                            <OrderPayments
                                orderId={isNew ? 0 : orderId}
                                dolg={sums.dolg}
                                ensureId={ensureId}
                                onChanged={onChanged}
                            />
                        </div>
                    </div>
                </Form>
            )}
            {printing && item && (
                <OrderPrint
                    order={{ ...item, docNum, docDate: docDate ? toApiDate(docDate) : item.docDate }}
                    onClose={() => setPrinting(false)}
                />
            )}
        </ResizableDrawer>
    );
};

// Числовое поле, сохраняющее значение по blur/Enter
const NumCell = ({ value, onCommit, ...props }) => {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    const commit = () => {
        if (v == null) setV(value);
        else if (Number(v) !== Number(value)) onCommit(v);
    };
    return (
        <InputNumber
            value={v}
            onChange={setV}
            onBlur={commit}
            onPressEnter={(e) => e.target.blur()}
            variant="filled"
            {...props}
        />
    );
};

const round2 = (v) => Math.round(num(v) * 100) / 100;

// Товары справочника: Map по id и опции селекта с ценой/единицей
const useProducts = () => {
    const { dicts } = useDicts();
    const productById = useMemo(() => new Map(dicts.products.map((p) => [p.id, p])), [dicts.products]);
    const productOptions = useMemo(() => dicts.products.map((p) => ({
        value: p.id,
        label: p.name,
        search: `${p.name} ${p.groupName ?? ''}`,
        product: p,
    })), [dicts.products]);
    return { products: dicts.products, productById, productOptions };
};

// Опция выбора товара: карточка «наименование / единица / цена»
const renderProductOption = ({ data: { product: p } }) => (
    <div className="order-opt">
        <div className="order-opt-main">
            <span className="order-opt-name">{p.name}</span>
            {p.groupName && <span className="order-opt-group">{p.groupName}</span>}
        </div>
        {p.unit && <span className="order-opt-unit">{p.unit}</span>}
        <span className="order-opt-price">{fmtMoney(p.price)}</span>
    </div>
);

// Drawer позиции заказа. record=null — новая позиция
const OrderItemDrawer = ({ record, onClose, onSave }) => {
    const { productById, productOptions } = useProducts();
    const [form] = Form.useForm();
    const productId = Form.useWatch('productId', form);
    const qty = Form.useWatch('qty', form);
    const price = Form.useWatch('price', form);
    const isNew = !record;
    const product = productById.get(productId);
    const syma = round2(num(qty) * num(price));
    const unit = product?.unit || record?.unit;

    const onProductChange = (id) => {
        const p = productById.get(id);
        if (p) form.setFieldsValue({ price: p.price });
    };

    // Ввод суммы пересчитывает цену (по завершении ввода)
    const [symaDraft, setSymaDraft] = useState(null);
    const applySyma = () => {
        if (symaDraft != null && num(qty) > 0) form.setFieldsValue({ price: round2(symaDraft / num(qty)) });
        setSymaDraft(null);
    };

    const step = (d) => form.setFieldsValue({ qty: Math.max(0, num(qty) + d) });

    const submit = () => form.validateFields()
        .then((values) => onSave({ productId: values.productId ?? null, qty: values.qty ?? 0, price: values.price ?? 0 }))
        .catch(ignoreValidation);

    // Не пускаем клавиши в карточку заказа; Ctrl/Cmd+Enter — сохранить позицию
    const onKeyDown = (e) => {
        e.stopPropagation();
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
    };

    return (
        <Drawer
            open
            width={440}
            closeIcon={null}
            extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}
            title={isNew ? 'Новая позиция' : 'Позиция заказа'}
            onClose={onClose}
            destroyOnClose
            className="order-item-drawer"
            footer={(
                <div className="order-item-footer">
                    <Button type="primary" className="order-item-ok" onClick={submit}>{isNew ? 'Добавить' : 'ОК'}</Button>
                    <Button onClick={onClose} className="order-item-footer-del">Отмена</Button>
                </div>
            )}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={submit}
                onKeyDown={onKeyDown}
                initialValues={{
                    productId: record?.productId ?? undefined,
                    qty: record ? num(record.qty) : 1,
                    price: record ? num(record.price) : 0,
                }}
            >
                <Form.Item name="productId" label="Товар" rules={[{ required: true, message: 'Выберите товар' }]}>
                    <Select
                        showSearch
                        autoFocus={isNew}
                        placeholder="Начните вводить название"
                        optionFilterProp="search"
                        options={productOptions}
                        optionRender={renderProductOption}
                        popupClassName="order-opt-popup"
                        onChange={onProductChange}
                    />
                </Form.Item>
                {product && (
                    <div className="order-item-product">
                        {product.groupName && <div className="order-muted">{product.groupName}</div>}
                        <div>
                            Цена по справочнику <b>{fmtMoney(product.price)}</b>{product.unit && ` / ${product.unit}`}
                            {num(price) !== num(product.price) && (
                                <button type="button" className="order-link" onClick={() => form.setFieldsValue({ price: product.price })}>
                                    подставить
                                </button>
                            )}
                        </div>
                        {product.description && <div className="order-item-desc">{product.description}</div>}
                    </div>
                )}
                <div className="order-item-grid">
                    <Form.Item label="Количество">
                        <div className="order-item-qty">
                            <Button icon={<MinusOutlined />} onClick={() => step(-1)} disabled={num(qty) < 1} />
                            <Form.Item name="qty" noStyle rules={[{ required: true }]}>
                                <InputNumber min={0} precision={3} controls={false} style={{ flex: 1 }} suffix={unit || undefined} />
                            </Form.Item>
                            <Button icon={<PlusOutlined />} onClick={() => step(1)} />
                        </div>
                    </Form.Item>
                    <Form.Item name="price" label="Цена" rules={[{ required: true, message: 'Укажите цену' }]}>
                        <InputNumber min={0} precision={2} controls={false} style={{ width: '100%' }} />
                    </Form.Item>
                </div>
                <Form.Item label="Сумма" extra="Изменение суммы пересчитает цену">
                    <InputNumber
                        min={0} precision={2} controls={false} size="large"
                        value={symaDraft ?? syma} onChange={setSymaDraft} onBlur={applySyma}
                        onPressEnter={(e) => { e.preventDefault(); applySyma(); }}
                        className="order-item-syma" style={{ width: '100%' }}
                    />
                </Form.Item>
                <button type="submit" hidden />
            </Form>
        </Drawer>
    );
};

// Позиции заказа: карточки, количество меняется на карточке, остальное — в drawer по клику
const OrderItems = ({ orderId, ensureId, onChanged }) => {
    const { productById } = useProducts();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | 'new' | record

    const load = useCallback((oid = orderId) => {
        if (!oid) { setItems([]); return; }
        serverFetch(`order/${oid}/items`).then(setItems).catch(showError);
    }, [orderId]);

    useEffect(() => { load(); }, [load]);

    const done = (order) => { load(order.id); onChanged?.(order); };

    const saveFromDrawer = (values) => {
        const isEdit = editing && editing !== 'new';
        return ensureId()
            .then((oid) => serverFetch(`order/${oid}/items${isEdit ? `/${editing.id}` : ''}`, {
                method: isEdit ? 'PUT' : 'POST',
                bodyData: isEdit ? { ...editing, ...values } : values,
            }))
            .then((order) => { setEditing(null); done(order); });
    };

    const update = (record, patch) => {
        const next = { ...record, ...patch };
        setItems((list) => list.map((i) => (i.id === record.id
            ? { ...next, syma: round2(num(next.qty) * num(next.price)) }
            : i)));
        serverFetch(`order/${orderId}/items/${record.id}`, { method: 'PUT', bodyData: next })
            .then(done)
            .catch((e) => { showError(e); load(); });
    };

    const remove = (record) => {
        serverFetch(`order/${orderId}/items/${record.id}`, { method: 'DELETE' })
            .then((order) => { setEditing(null); done(order); })
            .catch(showError);
    };

    const stop = (e) => e.stopPropagation();

    return (
        <Section
            icon={<ShoppingOutlined />}
            title={<>Позиции{items.length > 0 && <span className="order-count">{items.length}</span>}</>}
            extra={<Button size="small" type="link" icon={<PlusOutlined />} onClick={() => setEditing('new')}>Позиция</Button>}
        >
            <div className="order-items">
                {items.length > 0 && (
                    <div className="order-cards">
                        {items.map((r, idx) => {
                            const group = productById.get(r.productId)?.groupName;
                            return (
                                <div key={r.id} className="order-card" onClick={() => setEditing(r)} title="Открыть позицию">
                                    <span className="order-card-idx">{idx + 1}</span>
                                    <div className="order-card-title">
                                        <span className="order-card-name">{r.productName || '—'}</span>
                                        {group && <span className="order-card-group">{group}</span>}
                                    </div>
                                    <div className="order-card-qty" onClick={stop}>
                                        <Button
                                            size="small" type="text" icon={<MinusOutlined />}
                                            disabled={num(r.qty) <= 1}
                                            onClick={() => update(r, { qty: num(r.qty) - 1 })}
                                        />
                                        <NumCell
                                            value={num(r.qty)} min={0} precision={3} controls={false} size="small"
                                            onCommit={(qty) => update(r, { qty })}
                                        />
                                        <Button
                                            size="small" type="text" icon={<PlusOutlined />}
                                            onClick={() => update(r, { qty: num(r.qty) + 1 })}
                                        />
                                    </div>
                                    <span className="order-card-price">
                                        {r.unit ? `${r.unit} × ` : '× '}{fmtMoney(r.price)}
                                    </span>
                                    <span className="order-card-sum">{fmtMoney(r.syma)}</span>
                                    <span onClick={stop}>
                                        <Popconfirm title="Удалить позицию?" onConfirm={() => remove(r)} okText="Да" cancelText="Нет">
                                            <Button type="text" size="small" className="order-row-del" icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
                {items.length === 0 && <div className="task-empty">Позиций пока нет — нажмите «+ Позиция»</div>}
            </div>
            {editing !== null && (
                <OrderItemDrawer
                    record={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSave={saveFromDrawer}
                />
            )}
        </Section>
    );
};

const PAY_KINDS = [
    { value: 1, label: 'Оплата', hint: 'деньги от клиента', icon: <WalletOutlined /> },
    { value: -1, label: 'Возврат', hint: 'деньги клиенту', icon: <RollbackOutlined /> },
];

// Drawer платежа. record=null — новый; defaults — начальные значения нового; dolg — текущий долг заказа
const OrderPaymentDrawer = ({ record, defaults, dolg, onClose, onSave, onDelete }) => {
    const { dicts } = useDicts();
    const [form] = Form.useForm();
    const vid = Form.useWatch('vid', form);
    const syma = Form.useWatch('syma', form);
    const isNew = !record;
    const ret = vid === -1;

    // Долг без учёта редактируемого платежа и после сохранения
    const base = round2(dolg + (record ? num(record.syma) * (record.vid === -1 ? -1 : 1) : 0));
    const after = round2(base - num(syma) * (ret ? -1 : 1));
    const today = dayjs().startOf('day');

    const submit = () => form.validateFields()
        .then((values) => onSave({
            docDate: toApiDate(values.docDate),
            cashboxId: values.cashboxId ?? null,
            vid: values.vid ?? 1,
            syma: values.syma ?? 0,
            comment: values.comment ?? '',
        }))
        .catch(ignoreValidation);

    // Не пускаем клавиши в карточку заказа; Ctrl/Cmd+Enter — сохранить платёж
    const onKeyDown = (e) => {
        e.stopPropagation();
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
    };

    const cashboxOpts = dicts.cashboxes.map((c) => ({ value: c.id, label: c.name }));

    return (
        <Drawer
            open
            width={440}
            closeIcon={null}
            extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}
            title={isNew ? 'Новый платёж' : 'Платёж'}
            onClose={onClose}
            destroyOnClose
            className="order-item-drawer"
            footer={(
                <div className="order-item-footer">
                    <Button type="primary" className="order-item-ok" onClick={submit}>ОК</Button>
                    {!isNew && (
                        <Popconfirm title="Удалить платёж?" onConfirm={onDelete} okText="Да" cancelText="Нет">
                            <Button danger type="text" icon={<DeleteOutlined />} className="order-item-footer-del">Удалить</Button>
                        </Popconfirm>
                    )}
                    <Button onClick={onClose} className={isNew ? 'order-item-footer-del' : undefined}>Отмена</Button>
                </div>
            )}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={submit}
                onKeyDown={onKeyDown}
                initialValues={record ? {
                    docDate: toDayjs(record.docDate),
                    cashboxId: record.cashboxId ?? undefined,
                    vid: record.vid === -1 ? -1 : 1,
                    syma: num(record.syma),
                    comment: record.comment,
                } : defaults}
            >
                <Form.Item name="vid">
                    <PayKind />
                </Form.Item>
                <Form.Item
                    name="syma"
                    label="Сумма"
                    rules={[{ required: true, message: 'Укажите сумму' }]}
                    extra={!ret && base > 0 && num(syma) !== base && (
                        <button type="button" className="order-link" onClick={() => form.setFieldsValue({ syma: base })}>
                            Весь долг — {fmtMoney(base)}
                        </button>
                    )}
                >
                    <InputNumber
                        min={0} precision={2} size="large" controls={false} autoFocus
                        className="order-item-syma" style={{ width: '100%' }}
                    />
                </Form.Item>
                <div className={`order-pay-after${after > 0 ? ' debt' : ''}`}>
                    {after > 0 && <>После платежа долг <b>{fmtMoney(after)}</b></>}
                    {after === 0 && <><CheckCircleFilled /> Заказ будет оплачен полностью</>}
                    {after < 0 && <>Переплата <b>{fmtMoney(-after)}</b></>}
                </div>
                <Form.Item label="Дата">
                    <div className="order-pay-date">
                        <Form.Item name="docDate" noStyle rules={[{ required: true, message: 'Укажите дату' }]}>
                            <DatePicker format="DD.MM.YYYY" style={{ flex: 1 }} />
                        </Form.Item>
                        <div className="task-quick order-pay-quick">
                            <button type="button" onClick={() => form.setFieldsValue({ docDate: today })}>Сегодня</button>
                            <button type="button" onClick={() => form.setFieldsValue({ docDate: today.add(-1, 'day') })}>Вчера</button>
                        </div>
                    </div>
                </Form.Item>
                <Form.Item name="cashboxId" label="Касса">
                    {cashboxOpts.length > 0 && cashboxOpts.length <= 4
                        ? <Radio.Group optionType="button" options={cashboxOpts} className="order-pay-cashboxes" />
                        : <Select allowClear placeholder="—" options={cashboxOpts} />}
                </Form.Item>
                <Form.Item name="comment" label="Комментарий">
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="Например: перевод на карту, чек №…" />
                </Form.Item>
                <button type="submit" hidden />
            </Form>
        </Drawer>
    );
};

// Вид платежа: две плитки «Оплата» / «Возврат»
const PayKind = ({ value, onChange }) => (
    <div className="order-pay-kinds">
        {PAY_KINDS.map((k) => (
            <button
                key={k.value}
                type="button"
                className={`order-pay-kind${k.value === -1 ? ' ret' : ''}${value === k.value ? ' active' : ''}`}
                onClick={() => onChange?.(k.value)}
                aria-pressed={value === k.value}
            >
                <span className="order-pay-icon">{k.icon}</span>
                <span>
                    <b>{k.label}</b>
                    <span className="order-muted">{k.hint}</span>
                </span>
            </button>
        ))}
    </div>
);

// Платежи по заказу
const OrderPayments = ({ orderId, dolg, ensureId, onChanged }) => {
    const { dicts } = useDicts();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | { record, defaults }

    const load = useCallback((oid = orderId) => {
        if (!oid) { setItems([]); return; }
        serverFetch(`order/${oid}/payments`).then(setItems).catch(showError);
    }, [orderId]);

    useEffect(() => { load(); }, [load]);

    // Новый платёж: сумма по умолчанию — текущий долг
    const openAdd = () => {
        const lastCashbox = getStored('orderCashbox', null);
        const cashboxId = dicts.cashboxes.some((c) => c.id === lastCashbox) ? lastCashbox : dicts.cashboxes[0]?.id;
        setEditing({
            record: null,
            defaults: { docDate: dayjs(), cashboxId, vid: 1, syma: dolg > 0 ? round2(dolg) : null, comment: '' },
        });
    };

    const done = (order) => { setEditing(null); load(order.id); onChanged?.(order); };

    const onSave = (values) => {
        const record = editing?.record;
        if (values.cashboxId) setStored('orderCashbox', values.cashboxId);
        return ensureId()
            .then((oid) => serverFetch(`order/${oid}/payments${record ? `/${record.id}` : ''}`, {
                method: record ? 'PUT' : 'POST',
                bodyData: { ...(record ?? {}), ...values },
            }))
            .then(done);
    };

    const onDelete = (record) => {
        serverFetch(`order/${orderId}/payments/${record.id}`, { method: 'DELETE' })
            .then(done)
            .catch(showError);
    };

    return (
        <Section
            icon={<WalletOutlined />}
            title="Платежи"
            extra={<Button size="small" type="link" icon={<PlusOutlined />} onClick={openAdd}>Платёж</Button>}
        >
            <div className="order-payments">
                {items.length === 0 && <div className="task-empty">Платежей пока нет</div>}
                {items.map((r) => {
                    const ret = r.vid === -1;
                    return (
                        <div key={r.id} className="order-pay" onClick={() => setEditing({ record: r })}>
                            <span className={`order-pay-icon${ret ? ' ret' : ''}`}>{ret ? <RollbackOutlined /> : <WalletOutlined />}</span>
                            <div className="order-pay-main">
                                <div>
                                    <b>{ret ? 'Возврат' : 'Оплата'}</b>
                                    {r.cashboxName && <span className="order-muted"> · {r.cashboxName}</span>}
                                </div>
                                {r.comment && <div className="order-muted order-pay-comment">{r.comment}</div>}
                            </div>
                            <span className="order-muted">{fmtDate(r.docDate)}</span>
                            <span className={`order-pay-sum${ret ? ' ret' : ''}`}>{ret ? '−' : '+'}{fmtMoney(r.syma)}</span>
                            <span onClick={(e) => e.stopPropagation()}>
                                <Popconfirm title="Удалить платёж?" onConfirm={() => onDelete(r)} okText="Да" cancelText="Нет">
                                    <Button type="text" size="small" className="order-row-del" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </span>
                        </div>
                    );
                })}
            </div>
            {editing && (
                <OrderPaymentDrawer
                    record={editing.record}
                    defaults={editing.defaults}
                    dolg={dolg}
                    onClose={() => setEditing(null)}
                    onSave={onSave}
                    onDelete={() => onDelete(editing.record)}
                />
            )}
        </Section>
    );
};

export default Order;
